import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CpfCnpjInput } from '@/components/CpfCnpjInput'
import { ProfilePhotoCropModal } from '@/components/ProfilePhotoCropModal'
import { useInstitution } from '@/contexts/InstitutionContext'
import { useAuth } from '@/hooks/useAuth'
import { updateAdminProfile, updateInstitutionProfile } from '@/lib/auth'
import { getMe, updateInstitutionProfileViaClient } from '@/lib/api/auth.service'
import {
  PROFILE_IMAGE_ACCEPT,
  PROFILE_IMAGE_MAX_UPLOAD_BYTES,
  revokeObjectUrl,
  validateProfileImageFile,
} from '@/lib/profile-image'
import type { AuthGuard } from '@/lib/jwt'
import type { InstitutionMe } from '@/types/api'
import styles from './PerfilPage.module.css'

const VIA_CEP_URL = 'https://viacep.com.br/ws'

type TipoPessoa = 'pf' | 'pj'

interface ViaCepResponse {
  cep?: string
  logradouro?: string
  complemento?: string
  bairro?: string
  localidade?: string
  uf?: string
  erro?: boolean
}

type AddressValue = {
  postal_code?: string
  state?: string
  city?: string
  street?: string
  neighborhood?: string
  number?: string
  complement?: string
}

type AdminProfile = {
  name?: string
  corporate_name?: string
  document_cnpj?: string
  email?: string
  phone?: string
  address?: AddressValue
}

type InstitutionProfile = {
  person_type?: TipoPessoa
  name?: string
  corporate_name?: string
  document_cnpj?: string
  first_name?: string
  last_name?: string
  document_cpf?: string
  email?: string
  phone?: string
  gender?: string
  birth_date?: string
  logo_url?: string | null
  profile_photo_url?: string | null
  address?: AddressValue
}

const ESTADOS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function maskCep(value: string): string {
  const digits = onlyDigits(value).slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function extractAdmin(me: unknown): AdminProfile | null {
  if (!isRecord(me)) return null
  const data = isRecord(me.data) ? me.data : null
  const admin = (data && isRecord(data.admin) ? data.admin : null) ?? (isRecord(me.admin) ? me.admin : null)
  return isRecord(admin) ? (admin as AdminProfile) : null
}

function extractInstitution(me: unknown): InstitutionProfile | null {
  if (!isRecord(me)) return null
  const data = isRecord(me.data) ? me.data : null
  const institution =
    (data && isRecord(data.institution) ? data.institution : null) ??
    (isRecord(me.institution) ? me.institution : null)

  return isRecord(institution) ? (institution as InstitutionProfile) : null
}

function splitName(fullName: string): { first_name?: string; last_name?: string } {
  const trimmed = fullName.trim()
  if (!trimmed) return {}
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return { first_name: parts[0] }
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  }
}

function normalizeDate(value?: string): string {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function buildAddress(
  cep: string,
  endereco: string,
  bairro: string,
  cidade: string,
  estado: string,
  numero: string,
  complemento: string
): AddressValue | undefined {
  const postal_code = onlyDigits(cep)
  const street = endereco.trim()
  const neighborhood = bairro.trim()
  const city = cidade.trim()
  const state = estado.trim()
  const number = numero.trim()
  const complement = complemento.trim()

  if (!postal_code && !street && !neighborhood && !city && !state && !number && !complement) {
    return undefined
  }

  return {
    postal_code: postal_code || undefined,
    street: street || undefined,
    neighborhood: neighborhood || undefined,
    city: city || undefined,
    state: state || undefined,
    number: number || undefined,
    complement: complement || undefined,
  }
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  if (parts.length === 0) return 'LP'
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('')
}

function getValidationMessage(errors: unknown, field: string): string | null {
  if (!isRecord(errors)) return null
  const messages = errors[field]
  if (!Array.isArray(messages) || messages.length === 0) return null
  return typeof messages[0] === 'string' ? messages[0] : null
}

function extractErrorMessage(error: unknown): string {
  if (isRecord(error)) {
    const directValidation =
      getValidationMessage(error.errors, 'profile_photo') ??
      getValidationMessage(error.errors, 'address')

    if (directValidation) return directValidation

    const details = isRecord(error.details) ? error.details : null
    const nestedValidation =
      getValidationMessage(details?.errors, 'profile_photo') ??
      getValidationMessage(details?.errors, 'address')

    if (nestedValidation) return nestedValidation

    if (details && typeof details.message === 'string' && details.message.trim() !== '') {
      return details.message
    }

    if (typeof error.message === 'string' && !error.message.startsWith('API request failed')) {
      return error.message
    }
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return error.message
  }

  return 'Nao foi possivel salvar as alteracoes.'
}

function dispatchInstitutionProfileUpdated(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('legalprime:institution-profile-updated'))
}

export function PerfilTab() {
  const { guard, me, token, refreshMe } = useAuth()
  const { institutionId } = useInstitution()

  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>('pf')
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [documento, setDocumento] = useState('')
  const [nascimento, setNascimento] = useState('')
  const [sexo, setSexo] = useState('')
  const [cep, setCep] = useState('')
  const [cepLoading, setCepLoading] = useState(false)
  const [cepError, setCepError] = useState<string | null>(null)
  const [endereco, setEndereco] = useState('')
  const [numero, setNumero] = useState('')
  const [complemento, setComplemento] = useState('')
  const [bairro, setBairro] = useState('')
  const [cidade, setCidade] = useState('')
  const [estado, setEstado] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [institutionLoadError, setInstitutionLoadError] = useState<string | null>(null)
  const [institutionLoading, setInstitutionLoading] = useState(false)
  const [currentProfilePhotoUrl, setCurrentProfilePhotoUrl] = useState<string | null>(null)
  const [localProfilePhotoPreviewUrl, setLocalProfilePhotoPreviewUrl] = useState<string | null>(null)
  const [pendingProfilePhotoFile, setPendingProfilePhotoFile] = useState<File | null>(null)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [cropImageName, setCropImageName] = useState('foto-entidade.png')
  const [isCropModalOpen, setIsCropModalOpen] = useState(false)
  const [profilePhotoError, setProfilePhotoError] = useState<string | null>(null)

  const numeroInputRef = useRef<HTMLInputElement>(null)
  const profilePhotoInputRef = useRef<HTMLInputElement>(null)

  const guardMode: AuthGuard | null = guard ?? null
  const profileMode = useMemo<'admin' | 'institution'>(() => {
    if (guard === 'admin' && institutionId !== null) return 'institution'
    if (guard === 'admin') return 'admin'
    if (guard === 'institution') return 'institution'
    return 'admin'
  }, [guard, institutionId])

  const profilePhotoPreviewUrl = localProfilePhotoPreviewUrl ?? currentProfilePhotoUrl
  const profilePhotoInitials = useMemo(() => getInitials(nome || 'Entidade'), [nome])

  const clearCropImage = useCallback(() => {
    setCropImageSrc((current) => {
      revokeObjectUrl(current)
      return null
    })
    setIsCropModalOpen(false)
  }, [])

  const clearLocalProfilePhotoPreview = useCallback(() => {
    setLocalProfilePhotoPreviewUrl((current) => {
      revokeObjectUrl(current)
      return null
    })
  }, [])

  const clearProfilePhotoDraft = useCallback(() => {
    setPendingProfilePhotoFile(null)
    setProfilePhotoError(null)
    clearLocalProfilePhotoPreview()
    clearCropImage()
  }, [clearCropImage, clearLocalProfilePhotoPreview])

  const applyAdminProfile = useCallback((admin: AdminProfile) => {
    setTipoPessoa('pj')
    setNome(admin.corporate_name ?? admin.name ?? '')
    setEmail(admin.email ?? '')
    setTelefone(admin.phone ?? '')
    setDocumento(admin.document_cnpj ?? '')
    setNascimento('')
    setSexo('')

    const address = admin.address
    setCep(address?.postal_code ?? '')
    setEndereco(address?.street ?? '')
    setBairro(address?.neighborhood ?? '')
    setCidade(address?.city ?? '')
    setEstado(address?.state ?? '')
    setNumero(address?.number ?? '')
    setComplemento(address?.complement ?? '')
    setCurrentProfilePhotoUrl(null)
    clearProfilePhotoDraft()
  }, [clearProfilePhotoDraft])

  const applyInstitutionProfile = useCallback((institution: InstitutionProfile | InstitutionMe) => {
    const personType = (institution.person_type ?? 'pj') as TipoPessoa
    setTipoPessoa(personType)

    if (personType === 'pj') {
      setNome(institution.corporate_name ?? institution.name ?? '')
      setDocumento(institution.document_cnpj ?? '')
    } else {
      const fullName = [institution.first_name, institution.last_name].filter(Boolean).join(' ')
      setNome(fullName || institution.name || '')
      setDocumento(institution.document_cpf ?? '')
    }

    setEmail(institution.email ?? '')
    setTelefone(institution.phone ?? '')
    setSexo(institution.gender ?? '')
    setNascimento(normalizeDate(institution.birth_date))

    const address = institution.address
    setCep(address?.postal_code ?? '')
    setEndereco(address?.street ?? '')
    setBairro(address?.neighborhood ?? '')
    setCidade(address?.city ?? '')
    setEstado(address?.state ?? '')
    setNumero(address?.number ?? '')
    setComplemento(address?.complement ?? '')
    setCurrentProfilePhotoUrl(institution.profile_photo_url ?? null)
  }, [])

  useEffect(() => {
    return () => {
      revokeObjectUrl(localProfilePhotoPreviewUrl)
      revokeObjectUrl(cropImageSrc)
    }
  }, [cropImageSrc, localProfilePhotoPreviewUrl])

  useEffect(() => {
    if (guardMode === 'admin' && institutionId !== null) return

    if (guardMode === 'admin') {
      const admin = extractAdmin(me)
      if (admin) applyAdminProfile(admin)
      return
    }

    if (guardMode === 'institution') {
      const institution = extractInstitution(me)
      if (institution) applyInstitutionProfile(institution)
    }
  }, [applyAdminProfile, applyInstitutionProfile, guardMode, institutionId, me])

  useEffect(() => {
    if (guard !== 'admin' || institutionId === null) return

    let cancelled = false
    setInstitutionLoading(true)
    setInstitutionLoadError(null)

    getMe()
      .then((institution) => {
        if (cancelled) return
        applyInstitutionProfile(institution)
      })
      .catch(() => {
        if (!cancelled) {
          setInstitutionLoadError('Nao foi possivel carregar os dados da instituicao.')
        }
      })
      .finally(() => {
        if (!cancelled) setInstitutionLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [applyInstitutionProfile, guard, institutionId])

  const buscarCep = useCallback(async (cepDigits?: string) => {
    const digits = cepDigits ?? onlyDigits(cep)
    if (digits.length !== 8) {
      setCepError('Informe um CEP com 8 digitos.')
      return
    }

    setCepError(null)
    setCepLoading(true)

    try {
      const response = await fetch(`${VIA_CEP_URL}/${digits}/json/`)
      const data: ViaCepResponse = await response.json()

      if (data.erro === true) {
        setCepError('CEP nao encontrado.')
        return
      }

      setEndereco(data.logradouro ?? '')
      setBairro(data.bairro ?? '')
      setCidade(data.localidade ?? '')
      setEstado(data.uf ?? '')
      numeroInputRef.current?.focus()
    } catch {
      setCepError('Nao foi possivel buscar o CEP. Tente novamente.')
    } finally {
      setCepLoading(false)
    }
  }, [cep])

  const handleCepChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    const digits = onlyDigits(raw)
    const limited = digits.slice(0, 8)
    setCep(maskCep(limited))
    if (cepError) setCepError(null)
    if (limited.length === 8) {
      void buscarCep(limited)
    }
  }, [buscarCep, cepError])

  const handleProfilePhotoSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) return

    const validationError = validateProfileImageFile(file)
    if (validationError) {
      setProfilePhotoError(validationError)
      return
    }

    setProfilePhotoError(null)
    setCropImageName(file.name)
    setCropImageSrc((current) => {
      revokeObjectUrl(current)
      return URL.createObjectURL(file)
    })
    setIsCropModalOpen(true)
  }, [])

  const handleProfilePhotoConfirm = useCallback((file: File) => {
    setPendingProfilePhotoFile(file)
    setProfilePhotoError(null)
    setLocalProfilePhotoPreviewUrl((current) => {
      revokeObjectUrl(current)
      return URL.createObjectURL(file)
    })
    clearCropImage()
  }, [clearCropImage])

  const handleSave = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitError(null)
    setSubmitSuccess(null)

    if (!token) {
      setSubmitError('Sessao expirada. Faca login novamente.')
      return
    }

    if (
      pendingProfilePhotoFile &&
      pendingProfilePhotoFile.size > PROFILE_IMAGE_MAX_UPLOAD_BYTES
    ) {
      setSubmitError('A foto final ficou acima do limite de upload. Escolha outra imagem.')
      return
    }

    setIsSubmitting(true)

    try {
      const address = buildAddress(cep, endereco, bairro, cidade, estado, numero, complemento)

      if (profileMode === 'admin') {
        await updateAdminProfile({
          name: nome || undefined,
          corporate_name: nome || undefined,
          document_cnpj: onlyDigits(documento) || undefined,
          email: email || undefined,
          phone: telefone || undefined,
          address,
        })

        await refreshMe(token)
        setSubmitSuccess('Perfil atualizado com sucesso.')
        return
      }

      const basePayload = {
        person_type: tipoPessoa,
        name: nome || undefined,
        email: email || undefined,
        phone: telefone || undefined,
        address,
        profile_photo: pendingProfilePhotoFile ?? undefined,
      }

      let updatedInstitution: InstitutionProfile | InstitutionMe | null = null

      if (guard === 'admin' && institutionId !== null) {
        if (tipoPessoa === 'pj') {
          await updateInstitutionProfileViaClient({
            ...basePayload,
            corporate_name: nome || undefined,
            document_cnpj: onlyDigits(documento) || undefined,
          })
        } else {
          const { first_name, last_name } = splitName(nome)
          await updateInstitutionProfileViaClient({
            ...basePayload,
            first_name,
            last_name,
            document_cpf: onlyDigits(documento) || undefined,
            gender: sexo || undefined,
            birth_date: nascimento || undefined,
          })
        }

        updatedInstitution = await getMe()
        applyInstitutionProfile(updatedInstitution)
      } else {
        if (tipoPessoa === 'pj') {
          await updateInstitutionProfile({
            ...basePayload,
            corporate_name: nome || undefined,
            document_cnpj: onlyDigits(documento) || undefined,
          })
        } else {
          const { first_name, last_name } = splitName(nome)
          await updateInstitutionProfile({
            ...basePayload,
            first_name,
            last_name,
            document_cpf: onlyDigits(documento) || undefined,
            gender: sexo || undefined,
            birth_date: nascimento || undefined,
          })
        }

        updatedInstitution = (await refreshMe(token)) as InstitutionProfile | InstitutionMe | null
        if (updatedInstitution) {
          applyInstitutionProfile(updatedInstitution)
        }
      }

      clearProfilePhotoDraft()
      dispatchInstitutionProfileUpdated()
      setSubmitSuccess('Perfil atualizado com sucesso.')
    } catch (error) {
      setSubmitError(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }, [
    applyInstitutionProfile,
    bairro,
    cep,
    cidade,
    clearProfilePhotoDraft,
    complemento,
    documento,
    email,
    endereco,
    estado,
    guard,
    institutionId,
    nascimento,
    nome,
    numero,
    pendingProfilePhotoFile,
    profileMode,
    refreshMe,
    sexo,
    telefone,
    tipoPessoa,
    token,
  ])

  const canEditInstitutionPhoto = profileMode === 'institution'

  return (
    <div className={styles.card}>
      <form className={styles.form} onSubmit={handleSave}>
        {institutionLoadError && (
          <div className={styles.errorBanner} role="alert">
            {institutionLoadError}
          </div>
        )}

        {institutionLoading && (
          <div className={styles.loadingOverlay}>
            <span>Carregando dados da instituicao...</span>
          </div>
        )}

        {canEditInstitutionPhoto && (
          <section className={styles.photoSection}>
            <div className={styles.photoHeading}>
              <div>
                <h2 className={styles.sectionTitle}>Foto da entidade</h2>
                <p className={styles.sectionDescription}>
                  Escolha a imagem, ajuste o recorte quadrado e veja abaixo como ela ficara dentro do avatar redondo da plataforma.
                </p>
              </div>
            </div>

            <div className={styles.photoCard}>
              <div className={styles.photoPreviewColumn}>
                <div className={styles.photoPreviewFrame}>
                  {profilePhotoPreviewUrl ? (
                    <img
                      src={profilePhotoPreviewUrl}
                      alt="Previa da foto da entidade"
                      className={styles.photoPreviewImage}
                    />
                  ) : (
                    <span className={styles.photoPreviewFallback}>{profilePhotoInitials}</span>
                  )}
                </div>

                <div className={styles.photoMiniPreview}>
                  {profilePhotoPreviewUrl ? (
                    <img src={profilePhotoPreviewUrl} alt="" className={styles.photoPreviewImage} />
                  ) : (
                    <span className={styles.photoPreviewFallback}>{profilePhotoInitials}</span>
                  )}
                </div>
              </div>

              <div className={styles.photoContent}>
                <div className={styles.photoBadgeRow}>
                  <span className={styles.photoBadge}>
                    {pendingProfilePhotoFile
                      ? 'Nova foto pronta para envio'
                      : currentProfilePhotoUrl
                        ? 'Foto atual da entidade'
                        : 'Nenhuma foto definida'}
                  </span>
                  <span className={styles.photoMeta}>PNG final quadrado, exibicao circular</span>
                </div>

                <p className={styles.photoText}>
                  A imagem só será enviada quando você salvar as alterações do perfil.
                </p>

                <input
                  ref={profilePhotoInputRef}
                  type="file"
                  accept={PROFILE_IMAGE_ACCEPT}
                  className={styles.hiddenFileInput}
                  onChange={handleProfilePhotoSelection}
                />

                <div className={styles.photoActions}>
                  <button
                    type="button"
                    className={styles.photoButton}
                    onClick={() => profilePhotoInputRef.current?.click()}
                    disabled={isSubmitting || institutionLoading}
                  >
                    {profilePhotoPreviewUrl ? 'Trocar foto' : 'Escolher foto'}
                  </button>
                  <span className={styles.photoHint}>Recorte obrigatorio 1:1 antes do upload.</span>
                </div>

                {profilePhotoError && (
                  <span className={styles.photoError} role="alert">
                    {profilePhotoError}
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        <div className={styles.sectionsWrapper}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Dados pessoais</h2>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-tipo-pessoa">
                  Tipo de pessoa
                </label>
                <select
                  id="perfil-tipo-pessoa"
                  className={styles.input}
                  value={tipoPessoa}
                  onChange={(event) => setTipoPessoa(event.target.value as TipoPessoa)}
                  aria-label="Tipo de pessoa"
                  disabled={profileMode === 'admin'}
                >
                  <option value="pf">Pessoa Fisica</option>
                  <option value="pj">Pessoa Juridica</option>
                </select>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-nome">
                  {tipoPessoa === 'pj' ? 'Razao social' : 'Nome completo'}
                </label>
                <input
                  id="perfil-nome"
                  type="text"
                  className={styles.input}
                  placeholder={tipoPessoa === 'pj' ? 'Razao social da empresa' : 'Seu nome'}
                  autoComplete="name"
                  value={nome}
                  onChange={(event) => setNome(event.target.value)}
                />
              </div>

              {tipoPessoa === 'pf' && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="perfil-sexo">
                    Sexo
                  </label>
                  <select
                    id="perfil-sexo"
                    className={styles.input}
                    aria-label="Sexo"
                    value={sexo}
                    onChange={(event) => setSexo(event.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                    <option value="O">Outro</option>
                  </select>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-email">
                  E-mail
                </label>
                <input
                  id="perfil-email"
                  type="email"
                  className={styles.input}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              {tipoPessoa === 'pf' ? (
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="perfil-documento">
                      CPF
                    </label>
                    <CpfCnpjInput
                      id="perfil-documento"
                      mode="cpf"
                      className={styles.input}
                      placeholder="000.000.000-00"
                      invalidMessage="CPF invalido. Verifique os digitos."
                      value={documento}
                      onChange={(value) => setDocumento(value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="perfil-nascimento">
                      Data de nascimento
                    </label>
                    <input
                      id="perfil-nascimento"
                      type="date"
                      className={styles.input}
                      autoComplete="bday"
                      value={nascimento}
                      onChange={(event) => setNascimento(event.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="perfil-documento">
                    CNPJ
                  </label>
                  <CpfCnpjInput
                    id="perfil-documento"
                    mode="cnpj"
                    className={styles.input}
                    placeholder="00.000.000/0000-00"
                    invalidMessage="CNPJ invalido. Verifique os digitos."
                    value={documento}
                    onChange={(value) => setDocumento(value)}
                  />
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-telefone">
                  Telefone
                </label>
                <input
                  id="perfil-telefone"
                  type="tel"
                  className={styles.input}
                  placeholder="(00) 00000-0000"
                  autoComplete="tel"
                  value={telefone}
                  onChange={(event) => setTelefone(event.target.value)}
                />
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Endereco</h2>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-cep">
                  CEP
                </label>
                <input
                  id="perfil-cep"
                  type="text"
                  inputMode="numeric"
                  className={styles.input}
                  placeholder="00000-000"
                  autoComplete="postal-code"
                  value={cep}
                  onChange={handleCepChange}
                  disabled={cepLoading}
                  maxLength={9}
                  aria-describedby={cepError ? 'perfil-cep-error' : undefined}
                />
                {cepLoading && (
                  <span className={styles.cepHint} aria-live="polite">
                    Buscando...
                  </span>
                )}
                {cepError && (
                  <span id="perfil-cep-error" className={styles.cepError} role="alert">
                    {cepError}
                  </span>
                )}
                {!cepLoading && !cepError && endereco && (
                  <span className={styles.cepHint}>
                    Endereco preenchido. Informe o numero no campo abaixo.
                  </span>
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-endereco">
                  Endereco
                </label>
                <input
                  id="perfil-endereco"
                  type="text"
                  className={styles.input}
                  placeholder="Rua, avenida..."
                  autoComplete="street-address"
                  value={endereco}
                  onChange={(event) => setEndereco(event.target.value)}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="perfil-bairro">
                  Bairro
                </label>
                <input
                  id="perfil-bairro"
                  type="text"
                  className={styles.input}
                  placeholder="Bairro"
                  autoComplete="off"
                  value={bairro}
                  onChange={(event) => setBairro(event.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="perfil-cidade">
                    Cidade
                  </label>
                  <input
                    id="perfil-cidade"
                    type="text"
                    className={styles.input}
                    placeholder="Cidade"
                    autoComplete="address-level2"
                    value={cidade}
                    onChange={(event) => setCidade(event.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="perfil-estado">
                    Estado
                  </label>
                  <select
                    id="perfil-estado"
                    className={styles.input}
                    autoComplete="address-level1"
                    value={estado}
                    onChange={(event) => setEstado(event.target.value)}
                  >
                    <option value="">Selecione</option>
                    {ESTADOS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="perfil-numero">
                    Numero
                  </label>
                  <input
                    ref={numeroInputRef}
                    id="perfil-numero"
                    type="text"
                    inputMode="numeric"
                    className={styles.input}
                    placeholder="No"
                    autoComplete="off"
                    value={numero}
                    onChange={(event) => setNumero(event.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="perfil-complemento">
                    Complemento
                  </label>
                  <input
                    id="perfil-complemento"
                    type="text"
                    className={styles.input}
                    placeholder="Apto, bloco..."
                    autoComplete="off"
                    value={complemento}
                    onChange={(event) => setComplemento(event.target.value)}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        {submitError && (
          <div className={styles.errorBanner} role="alert">
            {submitError}
          </div>
        )}

        {submitSuccess && (
          <div className={styles.successBanner} role="status">
            {submitSuccess}
          </div>
        )}

        <button type="submit" className={styles.submit} disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : 'Salvar alteracoes'}
        </button>
      </form>

      <ProfilePhotoCropModal
        fileName={cropImageName}
        imageSrc={cropImageSrc}
        isOpen={isCropModalOpen}
        onCancel={clearCropImage}
        onConfirm={handleProfilePhotoConfirm}
      />
    </div>
  )
}
